/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useEffect, useRef } from 'preact/hooks';
import { DataState, CurrentKeyDetail } from 'src/UNSAFE_Collection';

/**
 * Hook to detect and handle when the current key has been removed
 * and try to find the next available current key.
 * TODO: put in its own private module as we should be able to share
 * between other Collection components.
 * @param dataState
 * @param currentKey
 * @param onChange
 */
export function useHandleRemoveCurrentKey<K, D>(
  dataState: DataState<K, D> | null,
  currentKey?: K,
  onChange?: (detail: CurrentKeyDetail<K>) => void
) {
  const prevDataState = useRef<DataState<K, D> | null>();

  useEffect(() => {
    const oldDataState = prevDataState.current;
    if (
      currentKey &&
      onChange &&
      oldDataState &&
      dataState &&
      oldDataState.offset === dataState.offset &&
      oldDataState !== dataState
    ) {
      const newKeys = dataState.data.map((dataMetadata) => {
        return dataMetadata.metadata.key;
      });
      // first check if currentKey exists in new data, if it is then we
      // don't need to do anything
      if (newKeys.indexOf(currentKey) === -1) {
        const oldKeys = oldDataState?.data.map((dataMetadata) => {
          return dataMetadata.metadata.key;
        });
        // figure out what the new currentKey should be
        let index = oldKeys.indexOf(currentKey);
        if (index > -1) {
          const backward = index === oldKeys.length - 1;
          while (index >= 0 && index < oldKeys.length) {
            index = backward ? index - 1 : index + 1;
            const newCurrentKey = oldKeys[index];
            if (newKeys.indexOf(newCurrentKey) > -1) {
              onChange({ value: newCurrentKey });
              break;
            }
          }
        }
      }
    }

    // update previous DataState
    prevDataState.current = dataState;
  }, [dataState, currentKey, onChange]);
}
